import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import logo3 from '../assets/logo3.png';
import './CSS/ImageConverter.css';

/* =================================================
   FINAL CLEANER — ROBUST THAI ID OCR POST-PROCESS
   ✔ Thai + English Issue & Expiry
   ✔ Backward scan (real OCR behavior)
================================================= */
const cleanThaiIDText = (text) => {
  if (!text) return '';

  const lines = text
    .split('\n')
    .map(l => l.replace('*', '').trim())
    .filter(Boolean);

  const output = [];
  let addressBuffer = [];
  let captureAddress = false;
  let waitingLastName = false;

  let thaiIssueDate = '';
  let engIssueDate = '';
  let thaiExpiryDate = '';
  let engExpiryDate = '';

  const isThaiDate = (l) =>
    /\d{1,2}\s[ก-ฮ\.]+\s25\d{2}/.test(l);

  const isEngDate = (l) =>
    /\d{1,2}\s[A-Za-z\.]+\s\d{4}/.test(l);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Remove OCR noise numbers
    if (/^\d{2,3}$/.test(line)) continue;

    /* ---------- ID NUMBER ---------- */
    if (line.includes('Identification Number')) {
      const num = line.replace(/[^0-9\s]/g, '').trim();
      output.push(`Identification Number ${num}`);
      continue;
    }

    /* ---------- NAMES ---------- */
    if (line.startsWith('ชื่อตัวและชื่อสกุล')) {
      output.push(line);
      continue;
    }

    if (line.startsWith('Name ')) {
      output.push(line);
      continue;
    }

    if (line.startsWith('Last name')) {
      waitingLastName = true;
      output.push('Last name');
      continue;
    }

    if (waitingLastName) {
      output[output.length - 1] += ` ${line}`;
      waitingLastName = false;
      continue;
    }

    /* ---------- DOB ---------- */
    if (line.startsWith('เกิดวันที่') || line.startsWith('Date of Birth')) {
      output.push(line);
      continue;
    }

    /* ---------- RELIGION ---------- */
    if (line.startsWith('ศาสนา')) {
      output.push(line);
      continue;
    }

    /* ---------- ADDRESS ---------- */
    if (line.startsWith('ที่อยู่')) {
      captureAddress = true;
      addressBuffer.push(line.replace('ที่อยู่', '').trim());
      continue;
    }

    if (captureAddress) {
      if (isThaiDate(line) || isEngDate(line)) {
        captureAddress = false;
        output.push(`ที่อยู่ ${addressBuffer.join(' ')}`);
        addressBuffer = [];
      } else {
        addressBuffer.push(line);
        continue;
      }
    }

    /* ---------- THAI ISSUE ---------- */
    if (line.includes('วันออกบัตร')) {
      for (let j = i - 1; j >= 0; j--) {
        if (isThaiDate(lines[j])) {
          thaiIssueDate = lines[j];
          break;
        }
      }
      continue;
    }

    /* ---------- THAI EXPIRY ---------- */
    if (line.includes('วันบัตรหมดอายุ')) {
      for (let j = i - 1; j >= 0; j--) {
        if (isThaiDate(lines[j])) {
          thaiExpiryDate = lines[j];
          break;
        }
      }
      continue;
    }

    /* ---------- ENG ISSUE ---------- */
    if (line.includes('Date of Issue')) {
      for (let j = i - 1; j >= 0; j--) {
        if (isEngDate(lines[j])) {
          engIssueDate = lines[j];
          break;
        }
      }
      continue;
    }

    /* ---------- ENG EXPIRY ---------- */
    if (line.includes('Date of Expiry')) {
      for (let j = i - 1; j >= 0; j--) {
        if (isEngDate(lines[j])) {
          engExpiryDate = lines[j];
          break;
        }
      }
      continue;
    }
  }

  if (addressBuffer.length) {
    output.push(`ที่อยู่ ${addressBuffer.join(' ')}`);
  }

  /* ---------- FINAL ORDER ---------- */
  if (thaiIssueDate) output.push(`วันออกบัตร ${thaiIssueDate}`);
  if (engIssueDate) output.push(`Date of Issue ${engIssueDate}`);
  if (thaiExpiryDate) output.push(`วันบัตรหมดอายุ ${thaiExpiryDate}`);
  if (engExpiryDate) output.push(`Date of Expiry ${engExpiryDate}`);

  return output.join('\n');
};

/* =================================================
   COMPONENT — UI PRESERVED
================================================= */
const IDOCR = (props) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cleanText, setCleanText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState(0);

  const COST_PER_EXTRACTION = 5;

  useEffect(() => {
    const state = props.location && props.location.state;
    if (state?.email && state.balance !== undefined) {
      setEmail(state.email);
      setBalance(state.balance);
    } else {
      fetchUserData();
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const res = await fetch('/user-details');
      if (res.ok) {
        const data = await res.json();
        setEmail(data.email);
        setBalance(data.balance);
      }
    } catch {}
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Only PNG or JPEG images allowed');
      return;
    }

    setSelectedFile(file);
    setCleanText('');
    setError('');

    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleExtractClick = async () => {
    if (!selectedFile) return;
    if (balance < COST_PER_EXTRACTION) {
      setError('Insufficient balance');
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await axios.post('/process-ocr', formData);
      setCleanText(cleanThaiIDText(res.data.text || ''));
      await updateBalance(balance - COST_PER_EXTRACTION);
    } catch {
      setError('OCR failed');
    } finally {
      setLoading(false);
    }
  };

  const updateBalance = async (newBalance) => {
    try {
      await fetch('/update-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, newBalance }),
      });
      setBalance(newBalance);
    } catch {}
  };

  return (
    <div style={{ padding: 20 }}>
      <img className="logo" src={logo} alt="Logo" />

      <div style={{ marginTop: 10, fontWeight: 'bold' }}>
        Email: {email || 'Loading...'} | Balance: {balance}
      </div>

      <h2 style={{ fontSize: '2.4rem', marginTop: 20 }}>
        Thai National ID OCR (Final)
      </h2>

      {/* FILE PICKER */}
      <label
        htmlFor="file-input"
        style={{
          display: 'inline-block',
          padding: '14px 28px',
          background: '#007BFF',
          color: '#fff',
          fontSize: '1.4rem',
          borderRadius: 8,
          cursor: 'pointer',
          marginTop: 20,
        }}
      >
        📂 Choose Image
      </label>

      <input
        id="file-input"
        type="file"
        accept=".png,.jpg,.jpeg"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* IMAGE PREVIEW */}
      {previewUrl && (
        <img
          src={previewUrl}
          alt="Preview"
          style={{
            maxWidth: '100%',
            marginTop: 20,
            borderRadius: 10,
          }}
        />
      )}

      <br />

      {/* EXTRACT BUTTON */}
      <button
        onClick={handleExtractClick}
        disabled={!selectedFile}
        style={{
          padding: '14px 30px',
          fontSize: '1.3rem',
          background: '#007BFF',
          color: '#fff',
          borderRadius: 8,
          border: 'none',
          marginTop: 20,
        }}
      >
        🔍 Extract & Clean
      </button>

      {loading && <p>⏳ Processing...</p>}

      {/* RESULT */}
      {cleanText && (
        <div style={{ marginTop: 40 }}>
          <h3>🪪 Clean Thai ID Data (Editable)</h3>

          <textarea
            value={cleanText}
            onChange={(e) => setCleanText(e.target.value)}
            rows={16}
            style={{
              width: '100%',
              padding: 20,
              fontSize: '1.2rem',
              lineHeight: '1.8',
              borderRadius: 10,
              background: '#f4f4f4',
            }}
          />

          <button
            onClick={() => navigator.clipboard.writeText(cleanText)}
            style={{
              marginTop: 20,
              padding: '14px 26px',
              fontSize: '1.2rem',
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            📋 Copy All
          </button>
        </div>
      )}

      <div style={{ marginTop: 60, textAlign: 'center' }}>
        <img src={logo2} alt="" height={40} />
        <img src={logo3} alt="" height={40} style={{ marginLeft: 20 }} />
        <div>New Wave Consulting Group</div>
      </div>
    </div>
  );
};

export default IDOCR;
